import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import {
  hasAlreadyReloadedForStaleChunks,
  isStaleChunkLoadMessage,
  markAppUpdateApplied,
  markReloadedForStaleChunks,
  notifyAppUpdateAvailable,
  subscribeAppUpdateApply,
  wasAppUpdateAlreadyApplied,
} from '@insite-store360/app-update';
import { concat, filter, first, fromEvent, interval } from 'rxjs';

/**
 * Watches the Angular service worker for a newer build, and recovers when a
 * deploy deleted hashed chunks the current page is still trying to lazy-load.
 * The header button is shown for a waiting SW version or a stale-chunk failure.
 * After click + hard reload the button stays hidden.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private applying = false;
  private recoveringChunks = false;

  constructor(
    private readonly sw: SwUpdate,
    appRef: ApplicationRef,
  ) {
    subscribeAppUpdateApply(() => void this.applyUpdate());
    this.listenForStaleChunks();

    if (!this.sw.isEnabled) {
      return;
    }

    this.sw.versionUpdates.subscribe((event: VersionEvent) => {
      this.handleVersionEvent(event);
    });

    this.sw.unrecoverable.subscribe(() => {
      void this.recoverFromStaleChunks();
    });

    const appIsStable$ = appRef.isStable.pipe(first((isStable) => isStable === true));
    concat(appIsStable$, interval(60 * 1000)).subscribe(() => {
      void this.sw.checkForUpdate();
    });

    fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'visible'))
      .subscribe(() => void this.sw.checkForUpdate());
  }

  private handleVersionEvent(event: VersionEvent): void {
    if (event.type !== 'VERSION_READY') {
      return;
    }

    const ready = event as VersionReadyEvent;
    const latestHash = ready.latestVersion?.hash;
    const currentHash = ready.currentVersion?.hash;

    if (!latestHash || latestHash === currentHash || wasAppUpdateAlreadyApplied(latestHash)) {
      return;
    }

    notifyAppUpdateAvailable(latestHash);
  }

  async applyUpdate(): Promise<void> {
    if (this.applying) {
      return;
    }
    this.applying = true;
    markAppUpdateApplied();
    try {
      if (this.sw.isEnabled) {
        await this.sw.checkForUpdate();
        await this.sw.activateUpdate();
      }
    } catch {
      // Still reload so the next document load uses the latest files.
    }
    this.reloadDocument();
  }

  private listenForStaleChunks(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener(
      'unhandledrejection',
      (event) => {
        if (isStaleChunkLoadMessage(event.reason)) {
          void this.recoverFromStaleChunks();
        }
      },
    );

    window.addEventListener(
      'error',
      (event) => {
        const target = event.target;
        const src =
          target instanceof HTMLScriptElement
            ? target.src
            : event.filename || '';
        if (
          isStaleChunkLoadMessage(event.message) ||
          isStaleChunkLoadMessage(event.error) ||
          (/\/(chunk|main|polyfills|runtime)-[A-Za-z0-9_-]+\.js(\?|$)/.test(src) &&
            (event.message || src))
        ) {
          void this.recoverFromStaleChunks();
        }
      },
      true,
    );
  }

  private async recoverFromStaleChunks(): Promise<void> {
    if (this.recoveringChunks || this.applying) {
      return;
    }
    this.recoveringChunks = true;
    notifyAppUpdateAvailable();

    if (hasAlreadyReloadedForStaleChunks()) {
      this.recoveringChunks = false;
      return;
    }

    markReloadedForStaleChunks();
    try {
      if (this.sw.isEnabled) {
        await this.sw.checkForUpdate();
        await this.sw.activateUpdate();
      }
    } catch {
      // Reload anyway — index.html is no-store so the next load gets new hashes.
    }
    this.reloadDocument();
  }

  private reloadDocument(): void {
    window.location.reload();
  }
}
